import React, { useCallback, useEffect, useRef, useState } from "react";
import "../../../styles/publicForm.css";
import useFormPageScrollbar from "../hooks/useFormPageScrollbar";
import useKeyboardAvoidance from "../hooks/useKeyboardAvoidance";
import useNoDocumentScroll from "../hooks/useNoDocumentScroll";
import PublicFormSkeleton from "./PublicFormSkeleton";
import type { PublicBrand } from "../../enquiries/types/enquiries.types";

/**
 * The frame every public form screen is drawn in: the practitioner's mark at
 * the top, the content under it, and one quiet line at the bottom saying who
 * runs the software.
 *
 * Deliberately not `AuthLayout`. That shell pins the *Naurist* logo top-left,
 * which is right for a screen where Naurist is the party being trusted and
 * wrong for every screen here: the person filling this in is answering their
 * dietitian, and a logo they do not recognise above a form asking for their
 * date of birth reads as a phishing page.
 *
 * The brand may be nothing at all — a workspace that set no name and uploaded
 * no logo can still share a link — so the lockup degrades: the logo falls back
 * to the brand name, and that falls back to nothing, leaving the screen's own
 * heading to carry it. It never falls back to Naurist's own mark.
 *
 * Naurist is not named anywhere on these screens — no footer, no credit, no
 * link. The page belongs to the practitioner from the top edge to the bottom
 * one, and the person filling it in has no relationship with us. Anything that
 * needs saying to them is said in the practitioner's voice or not at all.
 *
 * Left-aligned on a phone and centred from `sm:` up, which is what the design
 * asks for and also what reads best: a lockup pinned to the left edge of a
 * 1200px window looks lost, and one centred on a 360px one wastes the line.
 */

interface PublicFormShellProps {
  brand: PublicBrand;
  /**
   * The tinted background the form step sits on. The welcome and thank-you
   * screens are plain white — there are no cards on them to separate from it.
   */
  tone?: "plain" | "form";
  /**
   * Fills the screen and centres the content in what is left, for the two
   * screens that hold a single short message rather than a page of fields.
   */
  centered?: boolean;
  /**
   * Where the practitioner's mark sits.
   *
   * `top` pins it to the head of the page, above whatever follows — the form
   * itself, and the thank-you.
   *
   * `inline` makes it the first thing *inside* the centred block, so the mark
   * and the words under it move together as one group and sit in the middle of
   * the screen rather than being split between the top edge and the centre.
   * That is what the welcome screen wants: nothing else is on it, and a lockup
   * stranded at the top with a heading floating below it reads as two unrelated
   * things rather than as one greeting.
   */
  lockup?: "top" | "inline";
  /**
   * The gap under an inline lockup.
   *
   * `wide` is the welcome screen's, where the mark and a three-line greeting
   * are the whole page and the space between them is most of the composition.
   * `tight` is the form's: the mark is a label on a page of fields, and the
   * air the greeting wants would only push the cards off the bottom.
   */
  lockupGap?: "wide" | "tight";
  /**
   * How far the content is held off the edges of a phone.
   *
   * `wide` is for a screen carrying a few lines and nothing else — the welcome
   * — where a generous margin is what makes a 56px greeting read as a page
   * rather than as text jammed against the glass. The form does not get it:
   * its fields need every pixel of a narrow screen, and nine controls inside
   * a squeezed column is a worse trade than a tight gutter.
   *
   * Only the phone changes. From `sm:` up there is width to spare either way.
   */
  gutter?: "default" | "wide";
  /**
   * How wide the content column is allowed to grow.
   *
   * `narrow` is the pre-consultation form's, whose steps the design draws in a
   * column about 740px across. A card of four short answers stretched to a
   * thousand pixels puts its label and its value at opposite ends of the
   * reader's vision, and the wizard is read as a sequence — the eye should
   * come back to the same place on every step rather than sweep a new width.
   *
   * Only the ceiling moves. Below it the column is the width of the window
   * either way, so nothing about a phone changes.
   */
  width?: "default" | "narrow";
  /**
   * The screen is exactly as tall as the window and does not scroll — at all,
   * by any amount, in either direction.
   *
   * For the welcome, which is a greeting, a sentence and a button. There is
   * nothing under the fold to reach, so a scroll on it is never a reader going
   * somewhere; it is the viewport having been measured wrong, which on a phone
   * it routinely is. See `form-page-fit` in `publicForm.css` for what `100vh`
   * gets wrong and why this is not simply `overflow-hidden`.
   *
   * Not scrolled by the browser either: the document is held still while such
   * a screen is up, so there is no rubber band to give under the thumb — see
   * `useNoDocumentScroll`.
   *
   * The keyboard's room is not made either, because a screen with nothing to
   * type into never opens one.
   *
   * It costs the content the right to overflow: what does not fit is cut off
   * rather than scrolled to. That is what the `short:` rules on both welcome
   * screens are for — they hand back enough air on a small handset that there
   * is nothing left to cut.
   */
  fit?: boolean;
  /**
   * Held at the bottom edge of the screen, below everything else.
   *
   * For a screen whose one action belongs at the foot rather than under the
   * words — the pre-consultation welcome, whose Continue lands in the same
   * place as the wizard's Continue on every step after it, so that a reader
   * who presses one goes on pressing in the same spot rather than chasing a
   * button up the screen and back down again.
   *
   * Outside the block that centres the content, which is the whole point of
   * its being a prop rather than the last thing in `children`: an auto margin
   * cannot both centre a group and pin part of it to the floor.
   */
  footer?: React.ReactNode;
  /**
   * The brand has not been read yet, so the mark is drawn as a shimmer of the
   * size one occupies rather than left out.
   *
   * It belongs here and not in the page's own skeleton because the mark is the
   * shell's to place: on the welcome screen it is the first thing inside the
   * centred block, and a skeleton that drew its own would have to guess at
   * that. Drawn here it stands exactly where the logo lands, so nothing under
   * it moves when the read comes back.
   */
  loading?: boolean;
  children: React.ReactNode;
}

const GUTTERS: Record<"default" | "wide", string> = {
  default: "px-5 sm:px-8",
  wide: "px-10 sm:px-8",
};

/**
 * The form screen ends in a bar held at the bottom of the window, so it gets
 * no padding under it — a gap below the bar would leave a strip of page
 * showing beneath something meant to sit against the edge. The bar draws its
 * own bottom padding, safe area included.
 *
 * At both widths, not only on a phone. The pre-consultation form's bar is
 * `sticky` on a desktop window as well now — see `StepActions` — so the
 * foot of the page is the bar's to fill on either, and the enquiry form's
 * carries the same 1.5rem there for the same reason.
 */
const VERTICAL: Record<"plain" | "form", string> = {
  plain: "py-8 sm:py-8",
  form: "pt-8 pb-0 sm:pt-6 sm:pb-0",
};

/**
 * How long the page will wait for a logo file before showing itself anyway.
 *
 * There has to be a cap. The wait exists so the mark and the words under it
 * arrive together rather than the text landing first and the logo dropping in
 * on top of it a moment later — but a media host that is merely slow, or down,
 * must not be able to hold a practitioner's form closed. Four seconds is long
 * enough that a real logo on a poor connection still makes it, and short
 * enough that a dead one costs a pause rather than the form.
 */
const LOGO_WAIT_MS = 4000;

/** The brand name set as a wordmark, for a practitioner with no logo. */
const Wordmark: React.FC<{ name: string }> = ({ name }) => (
  <span className="text-[18px] sm:text-[22px] font-semibold text-[#0A4A52]">
    {name}
  </span>
);

const BrandLockup: React.FC<{
  brand: PublicBrand;
  loading?: boolean;
  /** The logo file has arrived, or has given up — see `settled` on the shell. */
  settled: boolean;
  onSettled: () => void;
}> = ({ brand, loading, settled, onSettled }) => {
  // A stored logo is fetched from the API's origin, and that fetch can fail —
  // the file moved, the media host is down. The name is a better answer than a
  // broken-image icon at the top of a form somebody is deciding whether to
  // trust. The same fallback `BrandMark` makes on the generated documents.
  const [broken, setBroken] = useState(false);

  const imageRef = useRef<HTMLImageElement>(null);

  // A logo already in the browser's cache is `complete` before React attaches
  // the handler below, so its `load` fired before anybody was listening and
  // will not fire again. Without this the page would sit behind the shimmer
  // for the full wait on every screen after the first.
  useEffect(() => {
    if (!settled && imageRef.current?.complete) onSettled();
  });

  const url = brand.logoUrl;
  const hasLogo = !!url && !broken;

  // Mounted while the shimmer is still up, and hidden rather than left out:
  // `display: none` is what makes the browser fetch the file without drawing
  // it, and the fetch is the thing being waited on.
  const image = hasLogo ? (
    <img
      ref={imageRef}
      key={url}
      src={url}
      alt={brand.name || "Logo"}
      onLoad={onSettled}
      onError={() => {
        setBroken(true);
        onSettled();
      }}
      // Height-bound, width free: a logo is any aspect ratio, and a fixed
      // width squashes a wordmark lockup into its own box.
      className={`h-11 sm:h-14 w-auto max-w-[70%] object-contain object-left sm:object-center ${
        settled ? "" : "hidden"
      }`}
    />
  ) : null;

  // The height a logo is bound to, at a width most of them sit near. Nothing
  // is known about the real one yet, so this is a place held rather than a
  // shape promised.
  if (loading || (hasLogo && !settled)) {
    return (
      <>
        <span
          aria-hidden="true"
          className="block h-11 w-36 animate-pulse rounded bg-[#EDF1F4] sm:h-14 sm:w-44"
        />
        {image}
      </>
    );
  }

  if (image) return image;

  return brand.name ? <Wordmark name={brand.name} /> : null;
};

const PublicFormShell: React.FC<PublicFormShellProps> = ({
  brand,
  tone = "plain",
  centered = false,
  lockup = "top",
  lockupGap = "wide",
  gutter = "default",
  width = "default",
  footer,
  fit = false,
  loading = false,
  children,
}) => {
  // Every screen of both forms is drawn here, so this is the one place that
  // has to know a phone keyboard is up — see `useKeyboardAvoidance`, which
  // sets the variable spent just below.
  useKeyboardAvoidance();

  // The page scrolls the document, so the thumb is the document's - styled to
  // the app's rather than left as the platform's grey bar down the edge of a
  // page that is otherwise entirely the practitioner's.
  useFormPageScrollbar();

  // And on a `fit` screen it does not scroll at all — not by a pixel, and not
  // the rubber band iOS gives a page with nowhere to go. `form-page-fit` below
  // only clips what is inside it; this is what holds the document itself. See
  // `useNoDocumentScroll`.
  useNoDocumentScroll(fit);

  /**
   * Which logo file has arrived — the url itself, not a flag, so a shell
   * handed a different brand starts waiting again rather than showing the new
   * one instantly on the strength of the old one having loaded.
   *
   * A screen with no logo to fetch is settled from the start, which is every
   * notice screen and every practitioner who uploaded nothing.
   */
  const [settledUrl, setSettledUrl] = useState<string | null>(null);
  const logoUrl = brand.logoUrl || null;
  const settled = !logoUrl || settledUrl === logoUrl;

  const onSettled = useCallback(() => setSettledUrl(logoUrl), [logoUrl]);

  useEffect(() => {
    if (settled) return;
    const timer = window.setTimeout(() => setSettledUrl(logoUrl), LOGO_WAIT_MS);
    return () => window.clearTimeout(timer);
  }, [settled, logoUrl]);

  /**
   * The screen, once there is a mark to head it with.
   *
   * Held back rather than drawn under a shimmer, because the complaint this
   * answers is that the two arrived apart: the form read lands first and the
   * logo is a second fetch behind it, so the greeting appeared and the mark
   * dropped in over it a moment later. They are one composition and they
   * appear together.
   *
   * The same skeleton the pages hand in while their own read is out, so the
   * two waits are one wait as far as anybody looking at it is concerned.
   */
  const body = settled ? children : <PublicFormSkeleton />;

  return (
    <div
      className={`${fit ? "form-page-fit" : "form-page"} w-full ${
        tone === "form" ? "bg-[#F7F9FB]" : "bg-white"
      }`}
    >
      {/*
        A screen's worth of column rather than `h-full`: it has to be able to
        fill the viewport for `centered` to mean anything, and to grow past it
        when the form is longer than the screen.

        A screen's worth is `100svh` and not `100vh`, which on a phone is the
        window with the browser's own toolbars pretended away — see
        `publicForm.css`, where both of these classes are written out and the
        difference is the whole of why they exist.

        Plus whatever a phone keyboard is covering, which is nothing at all
        until one is up — see `useKeyboardAvoidance`, which sets the variable.
        A screen's worth of column is otherwise the exact height of the window,
        so a step that fits has no overflow at all and a field at its foot
        cannot be scrolled clear of the keyboard however hard the page is
        pushed. Grown by the same amount, the page has somewhere to go.

        On the column rather than on the page around it, because the bar at the
        foot of both forms is `sticky` and can only travel as far as this
        element's bottom edge. Padding the page instead would leave the column
        ending at the keyboard's top edge — and the bar, pinned there, sitting
        over the very field the scroll had just brought into view. Grown here
        the bar stays where it belongs while typing: at the bottom of the page,
        behind the keyboard, out of the way.

        None of which applies to a `fit` screen: it is the height of the window
        exactly, there is nothing to type into on it, and the room a keyboard
        would want is room to scroll — which is the one thing it may not have.
      */}
      <div
        className={`mx-auto flex ${
          fit ? "form-column-fit" : "form-column"
        } w-full ${
          width === "narrow" ? "max-w-[740px]" : "max-w-[1024px]"
        } flex-col ${VERTICAL[tone]} ${GUTTERS[gutter]}`}
      >
        {lockup === "top" && (
          <header className="flex flex-shrink-0 justify-start sm:justify-center">
            <BrandLockup
              brand={brand}
              loading={loading}
              settled={settled}
              onSettled={onSettled}
            />
          </header>
        )}

        {/* Always a flex column, so a screen that wants to pin something to the
            foot — the thank-you's "you can close the browser" — can do it with
            `flex-1` on what comes before rather than by guessing a height. */}
        <main
          className={`flex flex-1 flex-col ${centered ? "justify-center" : ""} ${
            lockup === "top" ? "mt-8 sm:mt-4" : ""
          }`}
        >
          {lockup === "inline" ? (
            // Mark and content in one block, so they centre as a group rather
            // than the mark being pinned to the top edge with the content
            // floating in the middle — which reads as two unrelated things on a
            // wide screen, and is the whole reason this branch exists.
            //
            // `my-auto` on the block is what centres it. An auto margin collapses
            // to nothing when there is no free space, so a window too short for
            // the content scrolls from the top as normal — where centring the
            // column would put the mark out of reach above the scroll.
            <div className={`w-full ${centered ? "my-auto" : "sm:my-auto"}`}>
              <div
                // The wide gap is most of the welcome's composition and the
                // first thing it can spare: on a handset with no height to
                // give, half of it buys the button at the foot its place on a
                // screen that is not allowed to scroll. See `short:` in
                // `tailwind.config.js`.
                className={`flex justify-start sm:justify-center ${
                  lockupGap === "tight"
                    ? "mb-8 sm:mb-4"
                    : "mb-20 short:mb-9 sm:mb-14"
                }`}
              >
                <BrandLockup
                  brand={brand}
                  loading={loading}
                  settled={settled}
                  onSettled={onSettled}
                />
              </div>
              {body}
            </div>
          ) : (
            body
          )}

          {/* The floor of the screen. `flex-shrink-0` so it keeps its height
              against a block above it that is asking for more room than there
              is — on a `fit` screen the overflow is cut off, and what gets cut
              must never be the one thing there is to press. */}
          {footer && <div className="flex-shrink-0">{footer}</div>}
        </main>
      </div>
    </div>
  );
};

export default PublicFormShell;
